import {
  LayoutDashboard,
  Inbox,
  Kanban,
  Database,
  ChartNoAxesCombinedIcon,
  Workflow,
  Megaphone,
  Sparkles,
  Users,
  Building2,
  Smartphone,
  Waypoints,
  Handshake,
  Gift,
  GraduationCap,
  Bell,
  LifeBuoy,
  Circle,
} from "lucide-react";
import { navItem } from "@touchworks/shared/platform/navigation.js";
import { AnimatedIcon } from "@/components/internal/AnimatedIcon";

/**
 * O ícone de cada item de menu — o mesmo que o menu desenha.
 *
 * O catálogo compartilhado guarda o NOME do ícone, porque a API também o lê e
 * lá não existe React. A resolução para componente mora aqui, e é usada tanto
 * pelo menu quanto pelo cartão de caminho que o Lucas mostra: se o ícone do
 * chat fosse outro, a pessoa varreria a barra lateral procurando o errado.
 */

const NAV_ICON = {
  LayoutDashboard,
  Inbox,
  Kanban,
  Database,
  ChartNoAxesCombinedIcon,
  Workflow,
  Megaphone,
  Sparkles,
  Users,
  Building2,
  Smartphone,
  Waypoints,
  Handshake,
  Gift,
  GraduationCap,
  Bell,
  LifeBuoy,
};

/**
 * O ícone animado (contorno, Lottie) de cada nome do catálogo, quando existe. O
 * arquivo mora em `public/assets/sidebar-icons/<nome>.json` e é gerado por
 * `scripts/lottie/build-sidebar-icons.mjs`. Nome sem entrada aqui segue com o
 * ícone do Lucide.
 */
const NAV_LOTTIE = {
  LayoutDashboard: "dashboard",
  Inbox: "messages",
  Kanban: "kanban",
  Smartphone: "channels",
  ChartNoAxesCombinedIcon: "reports",
  Workflow: "automation",
  Megaphone: "broadcasts",
  Waypoints: "integrations",
  Gift: "referrals",
  GraduationCap: "program",
  Users: "users",
  Building2: "departments",
  Handshake: "handshake",
  Sparkles: "contents",
  Bell: "notifications",
  LifeBuoy: "guides",
};

/** @param {string} name — o nome guardado no catálogo */
export function navIcon(name) {
  return NAV_ICON[name] || Circle;
}

/** @param {string} name o nome guardado no catálogo; devolve `null` se o ícone não tem versão animada */
export function navLottie(name) {
  return NAV_LOTTIE[name] || null;
}

const animatedByName = new Map();

/**
 * O ícone como componente de qualquer tamanho, no mesmo desenho do menu: animado
 * quando existe, Lucide quando não. Parado (não toca sozinho). O cartão de caminho
 * do Lucas usa este para mostrar o ícone que a pessoa vai achar na barra lateral.
 *
 * O componente é guardado por nome: criar um novo a cada render remontaria o
 * ícone (e o player) toda vez.
 *
 * @param {string} name o nome guardado no catálogo
 */
export function navIconAnimated(name) {
  const lottie = navLottie(name);
  if (!lottie) return navIcon(name);
  if (!animatedByName.has(name)) {
    const Fallback = navIcon(name);
    const Animated = ({ className }) => <AnimatedIcon name={lottie} fallback={Fallback} className={className} />;
    Animated.displayName = `NavIcon(${name})`;
    animatedByName.set(name, Animated);
  }
  return animatedByName.get(name);
}

/** @param {string} key — a chave do item de menu */
export function navIconByKey(key) {
  const item = navItem(key);
  return item ? navIcon(item.icon) : Circle;
}

/** Os itens já com o componente resolvido (e o ícone animado, quando há), para o menu montar a lista. */
export function withIcons(items) {
  return items.map((item) => ({ ...item, icon: navIcon(item.icon), lottie: navLottie(item.icon) }));
}

export default navIcon;
